import { PencilSquareIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../components/shared/Buttons"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import useComponentVisible from "../../hooks/useComponentVisible"

const UPDATE_BLOG_SETTINGS_MUTATION = `
  mutation UpdateBlogSettings($blogPageTitle: String) {
    updateBlogSettings(blogPageTitle: $blogPageTitle) {
      result
      blogPageTitle
      errors
    }
  }
`

const BlogPageTitleButton = ({ initialTitle = "", onSaved }) => {
  const [blogPageTitle, setBlogPageTitle] = useState(initialTitle || "")
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()
  const [, updateBlogSettings] = useMutation(UPDATE_BLOG_SETTINGS_MUTATION)
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false)

  useEffect(() => {
    if (!isComponentVisible) return undefined

    setBlogPageTitle(initialTitle || "")

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsComponentVisible(false)
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isComponentVisible, initialTitle, setIsComponentVisible])

  const closePopover = () => setIsComponentVisible(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const trimmed = blogPageTitle.trim()
      const result = await updateBlogSettings({
        blogPageTitle: trimmed || null
      })
      if (result.data?.updateBlogSettings?.result === "success") {
        const savedTitle = result.data.updateBlogSettings.blogPageTitle || ""
        onSaved?.(savedTitle)
        showToast({ content: "Blog page title saved", type: "success" })
        closePopover()
      } else {
        showToast({
          content: result.data?.updateBlogSettings?.errors?.[0] || "Failed to save settings",
          type: "error"
        })
      }
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
      showToast({ content: "Failed to save settings", type: "error" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <Button
        type="secondary"
        onClick={() => setIsComponentVisible((open) => !open)}
        aria-expanded={isComponentVisible}
        aria-haspopup="dialog">
        <span className="flex items-center gap-1.5">
          <PencilSquareIcon className="size-4" aria-hidden />
          Edit page title
        </span>
      </Button>

      {isComponentVisible && (
        <div
          className="absolute right-0 top-full z-20 mt-2 w-80 rounded-lg border border-gray-light bg-white p-4 shadow-lg sm:w-96"
          role="dialog"
          aria-label="Edit blog page title">
          <Typography variant="h5" className="mb-1 font-bold text-builder-primary">
            Blog page title
          </Typography>
          <Typography variant="small" className="mb-3 block text-builder-secondary">
            Shown on /blog and in your site nav. Leave blank to use &quot;Blog&quot;.
          </Typography>
          <input
            type="text"
            className="mb-4 w-full rounded-lg border border-builder-border bg-white px-3 py-2 text-sm text-builder-primary outline-none placeholder:text-builder-border focus:border-teal focus:ring-1 focus:ring-teal"
            placeholder="e.g. My articles about wellness"
            value={blogPageTitle}
            onChange={(e) => setBlogPageTitle(e.target.value)}
            maxLength={120}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="link" onClick={closePopover} disabled={saving}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleSave} disabled={saving}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

BlogPageTitleButton.propTypes = {
  initialTitle: PropTypes.string,
  onSaved: PropTypes.func
}

export default BlogPageTitleButton
